/**
 * UserModeToggle (iter76) — subtle "Switch to Advanced / Simple" affordance
 * that lives in the top-right control cluster on every subpage. Coach/admin
 * users don't see it (UserModeContext force-swaps them).
 */
import { Settings2, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUserMode } from "@/context/UserModeContext";

export default function UserModeToggle({ compact = false, homeOnly = false, noHome = false }) {
  const { user } = useAuth();
  const { mode, toggle, isForced, isSimple } = useUserMode();

  // Coaches + admins are locked to advanced — no toggle for them.
  if (isForced || !user || user.role !== "student") return null;
  if (homeOnly && !isSimple) return null;

  return (
    <div className="flex items-center gap-2" data-testid={homeOnly ? "user-home-link-wrap" : "user-mode-toggle-wrap"}>
      {isSimple && !noHome && (
        <Link
          to="/dashboard"
          data-testid="simple-home-link"
          aria-label="Back to Simple home"
          title="Back to Simple home"
          className={
            "inline-flex items-center justify-center border transition-colors " +
            (compact ? "w-9 h-9" : "w-10 h-10") +
            " hover:brightness-110"
          }
          style={{
            background: "var(--surface)",
            borderColor: "var(--border)",
            color: "var(--text)",
            borderRadius: 999,
          }}
        >
          <Home className={compact ? "w-4 h-4" : "w-4 h-4"} strokeWidth={1.75} />
        </Link>
      )}
      {!homeOnly && <button
        type="button"
        onClick={toggle}
        data-testid="user-mode-toggle"
        aria-label={mode === "simple" ? "Switch to Advanced Mode" : "Switch to Simple Mode"}
        title={mode === "simple" ? "Switch to Advanced Mode" : "Switch to Simple Mode"}
        className={
          "inline-flex items-center gap-1.5 border transition-colors " +
          (compact ? "h-9 px-3 text-[10px]" : "h-10 px-4 text-xs") +
          " hover:brightness-110"
        }
        style={{
          background: "var(--surface)",
          borderColor: "var(--border)",
          color: mode === "simple" ? "var(--teal)" : "var(--text)",
          borderRadius: 999,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        <Settings2 className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={1.75} />
        <span>{mode === "simple" ? "Switch to Advanced Mode" : "Switch to Simple Mode"}</span>
      </button>}
    </div>
  );
}
